import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { PlusCircle, MinusCircle, Save, ListChecks, Pencil, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createEmptyOption = () => ({ text: "", weight: "" });

const emptyOptions = [createEmptyOption()];
const stripPortableSuffix = (text = "") => text.replace(/\s+\([a-c]\)\s*$/i, "").trim();
const isPortableSectionName = (name = "") =>
  String(name || "").toLowerCase().includes("portable skills");

const Admin = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list"); // list | section | question
  const [sectionForm, setSectionForm] = useState({
    name: "",
    description: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [viewSectionId, setViewSectionId] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    sectionId: "",
    question_text: "",
    options: emptyOptions,
    multi_part: false,
  });
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [questionSaving, setQuestionSaving] = useState(false);
  const [sectionQuestions, setSectionQuestions] = useState({});
  const [loadingSectionQuestions, setLoadingSectionQuestions] = useState({});
  const [questionTablePage, setQuestionTablePage] = useState({});
  const [sectionPage, setSectionPage] = useState(1);

  const email = localStorage.getItem("userEmail") || "";
  const role = localStorage.getItem("userRole");

  useEffect(() => {
    if (!email) {
      navigate("/register");
      return;
    }
    if (role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    fetchSections();
  }, [email, role, navigate]);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/sections`, {
        params: { admin_email: email },
      });
      setSections(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load sections");
    } finally {
      setLoading(false);
    }
  };


  const loadQuestions = async (sectionId) => {
    setLoadingSectionQuestions((prev) => ({
      ...prev,
      [sectionId]: true,
    }));
    try {
      const response = await axios.get(`${API}/sections/${sectionId}/questions`, {
        params: { admin_email: email },
        headers: { "x-admin-email": email }
      });
      setSectionQuestions((prev) => ({
        ...prev,
        [sectionId]: response.data.questions || [],
      }));
      setQuestionTablePage((prev) => ({
        ...prev,
        [sectionId]: 1,
      }));
      // preselect for editing list
      if (questionForm.sectionId !== sectionId) {
        setQuestionForm((prev) => ({
          ...prev,
          sectionId,
        }));
      }
    } catch (error) {
      toast.error("Could not load questions");
      setSectionQuestions((prev) => ({
        ...prev,
        [sectionId]: [],
      }));
    } finally {
      setLoadingSectionQuestions((prev) => ({
        ...prev,
        [sectionId]: false,
      }));
    }
  };

  const handleSectionChange = (e) => {
    setSectionForm({ ...sectionForm, [e.target.name]: e.target.value });
  };

  const handleQuestionChange = (e) => {
    setQuestionForm({ ...questionForm, [e.target.name]: e.target.value });
  };

  const handleOptionChange = (index, field, value) => {
    const updated = questionForm.options.map((opt, idx) =>
      idx === index ? { ...opt, [field]: value } : opt
    );
    setQuestionForm({ ...questionForm, options: updated });
  };

  const addOption = () => {
    setQuestionForm((prev) => ({
      ...prev,
      options: [...prev.options, createEmptyOption()],
    }));
  };

  const removeOption = (index) => {
    setQuestionForm((prev) => ({
      ...prev,
      options:
        prev.options.length > 1
          ? prev.options.filter((_, idx) => idx !== index)
          : prev.options,
    }));
  };

  const submitSection = async (e) => {
    e.preventDefault();
    if (!sectionForm.name) {
      toast.error("Section name is required");
      return;
    }
    try {
      if (editingId) {
        await axios.patch(`${API}/admin/sections/${editingId}`, {
          admin_email: email,
          name: sectionForm.name,
          description: sectionForm.description,
        });
        toast.success("Section updated");
      } else {
        const createRes = await axios.post(`${API}/admin/sections`, {
          admin_email: email,
          name: sectionForm.name,
          description: sectionForm.description,
        });
        toast.success("Section created");
        const newId = createRes.data?.id;
        if (newId) {
          setEditingId(newId);
          setViewSectionId(newId);
          setQuestionForm((prev) => ({
            ...prev,
            sectionId: newId,
            multi_part: isPortableSectionName(sectionForm.name || ""),
          }));
          loadQuestions(newId);
        }
      }
      fetchSections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create section");
    }
  };

  const submitQuestion = async (e) => {
    e.preventDefault();
    if (!questionForm.sectionId) {
      toast.error("Choose a section first");
      return;
    }
    if (!questionForm.question_text) {
      toast.error("Question text is required");
      return;
    }
    if (!questionForm.multi_part && !questionForm.options.length) {
      toast.error("At least one option is required");
      return;
    }
    if (!questionForm.multi_part && questionForm.options.some((opt) => !opt.text?.trim())) {
      toast.error("All options must have text");
      return;
    }
    const targetSectionId = questionForm.sectionId;
    setQuestionSaving(true);
    try {
      if (editingQuestionId) {
        await axios.patch(`${API}/admin/questions/${editingQuestionId}`, {
          admin_email: email,
          question_text: questionForm.question_text,
          ...(questionForm.multi_part ? { multi_part: true } : { options: questionForm.options }),
        });
        toast.success("Question updated");
        exitQuestionEdit();
      } else {
        await axios.post(`${API}/admin/sections/${questionForm.sectionId}/questions`, {
          admin_email: email,
          question_text: questionForm.question_text,
          options: questionForm.multi_part ? [] : questionForm.options,
          multi_part: questionForm.multi_part,
        });
        toast.success("Question added");
      }
      if (!editingQuestionId) {
        setQuestionForm({
          sectionId: questionForm.sectionId,
          question_text: "",
          options: emptyOptions,
          multi_part: isPortableSection,
        });
        setEditingQuestionId(null);
      }
      loadQuestions(targetSectionId);
      fetchSections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add question");
    } finally {
      setQuestionSaving(false);
    }
  };

  const startCreateNew = () => {
    setViewMode("section");
    setEditingId(null);
    setViewSectionId(null);
    setSectionForm({ name: "", description: "" });
    setQuestionForm({ sectionId: "", question_text: "", options: emptyOptions, multi_part: false });
  };

  const startEditSection = (section) => {
    setViewMode("section");
    setEditingId(section.id);
    const isPortable = isPortableSectionName(section.name || "");
    setSectionForm({
      name: section.name || "",
      description: section.description || "",
    });
    setQuestionForm((prev) => ({ ...prev, sectionId: section.id, multi_part: isPortable }));
    setViewSectionId(section.id);
    loadQuestions(section.id);
  };

  const startEditQuestion = (sectionId, question) => {
    const section = sections.find((s) => s.id === sectionId);
    const isPortable = isPortableSectionName(section?.name || "") || String(question?.trait || "") === "portable_group";
    const mappedOptions =
      question.options?.map((opt) => ({
        text: opt.text ?? opt,
        weight: opt.weight ?? "",
      })) || [];
    setViewMode("question");
    setQuestionForm({
      sectionId,
      question_text: isPortable ? stripPortableSuffix(question.question_text) : question.question_text,
      options: mappedOptions.length ? mappedOptions : [createEmptyOption()],
      multi_part: isPortable,
    });
    setEditingQuestionId(question.id);
  };

  const startEditPortableGroup = (sectionId, group) => {
    if (!group?.primary) return;
    startEditQuestion(sectionId, group.primary);
  };

  const exitQuestionEdit = () => {
    setEditingQuestionId(null);
    setQuestionForm({
      sectionId: activeSectionId || "",
      question_text: "",
      options: emptyOptions,
      multi_part: isPortableSectionName(activeSection?.name || ""),
    });
    setViewMode("section");
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm("Delete this section and all its questions?")) return;
    try {
      await axios.delete(`${API}/admin/sections/${sectionId}`, {
        params: { admin_email: email },
        headers: { "x-admin-email": email },
      });
      toast.success("Section deleted");
      if (viewSectionId === sectionId) {
        setViewSectionId(null);
        setViewMode("list");
      }
      fetchSections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete section");
    }
  };

  const deleteQuestion = async (sectionId, questionId, groupIds = null) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      const idsToDelete = groupIds && groupIds.length ? groupIds : [questionId];
      for (const id of idsToDelete) {
        await axios.delete(`${API}/admin/questions/${id}`, {
          params: { admin_email: email },
          headers: { "x-admin-email": email },
        });
      }
      toast.success("Question deleted");
      loadQuestions(sectionId);
      fetchSections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete question");
    }
  };

  const activeSectionId = viewSectionId || editingId || questionForm.sectionId;
  const activeSection = sections.find((s) => s.id === activeSectionId);
  const isPortableSection = isPortableSectionName(activeSection?.name || "");

  const buildPortableGroups = (list = []) =>
    list
      .filter((q) => String(q.trait || "") === "portable_group")
      .map((q, idx) => ({
        baseText: q.question_text,
        questions: [q],
        primary: q,
        firstIndex: idx,
      }));
  const pageSize = 6;
  const sectionPageSize = 5;
  const sectionTotalPages = Math.max(1, Math.ceil(sections.length / sectionPageSize));
  const sectionPaged = sections.slice(
    (sectionPage - 1) * sectionPageSize,
    sectionPage * sectionPageSize
  );
  const formsOpen = viewMode !== "list";

  return (
    <div>
      <Navbar />
      <div className="dashboard-page">
        <div className="dashboard-container">
          <div className="dashboard-header">
            <div>
              <h1>Manage Sections</h1>
              <p>Create sections and manage questions with weighted options.</p>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
                <button className="btn-secondary active-tab">
                  Sections
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate("/admin-results")}
                >
                  Assessee Reports
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate("/admin-students")}
                >
                  Assessees
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate("/admin-config")}
                >
                  Configuration
                </button>
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              {!formsOpen && (
                <button className="btn-secondary" onClick={startCreateNew} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                  <PlusCircle size={18} />
                  New Section
                </button>
              )}
              {formsOpen && (
                <button className="btn-secondary" onClick={() => setViewMode("list")} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                  Back to Sections
                </button>
              )}
            </div>
          </div>

          {viewMode === "list" && (
            <div className="section-list">

              {loading ? (
                <div className="auth-card"><p>Loading sections...</p></div>
              ) : sections.length === 0 ? (
                <div className="empty-state">
                  <h3>No sections yet</h3>
                  <p>Create a section to get started.</p>
                </div>
              ) : (
                <>
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Questions</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sectionPaged.map((section) => (
                          <tr key={section.id}>
                            <td>{section.name}</td>
                            <td style={{ maxWidth: "320px" }}>{section.description || "No description"}</td>
                            <td>{section.question_count}</td>
                            <td style={{ display: "flex", gap: "0.5rem" }}>
                              <button
                                className="btn-secondary"
                                onClick={() => startEditSection(section)}
                              >
                                <Pencil size={16} />
                                Edit
                              </button>
                              <button
                                className="btn-secondary"
                                onClick={() => deleteSection(section.id)}
                              >
                                <Trash2 size={16} />
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {sections.length > sectionPageSize && (
                    <div className="table-pagination">
                      <button
                        className="btn-secondary"
                        onClick={() => setSectionPage(Math.max(1, sectionPage - 1))}
                        disabled={sectionPage === 1}
                      >
                        Previous
                      </button>
                      <span>
                        Page {sectionPage} of {sectionTotalPages}
                      </span>
                      <button
                        className="btn-secondary"
                        onClick={() => setSectionPage(Math.min(sectionTotalPages, sectionPage + 1))}
                        disabled={sectionPage >= sectionTotalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}


          {viewMode === "section" && (
            <div className="admin-grid">
              <form className="panel-card" onSubmit={submitSection}>
                <div className="panel-card-header">
                  <div>
                    <h3>{editingId ? "Edit Section" : "Create Section"}</h3>
                    <p>Name and description.</p>
                  </div>
                </div>

                <label className="form-label">Section name</label>
                <input
                  className="form-input"
                  name="name"
                  value={sectionForm.name}
                  onChange={handleSectionChange}
                  placeholder="e.g. Reasoning"
                  required
                />

                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  name="description"
                  rows="3"
                  value={sectionForm.description}
                  onChange={handleSectionChange}
                  placeholder="What does this section measure?"
                />

                <button className="btn-primary" type="submit">
                  <Save size={16} />
                  {editingId ? "Update Section" : "Save Section"}
                </button>
              </form>

              <form className="panel-card" onSubmit={submitQuestion}>
                <div className="panel-card-header">
                  <div>
                    <h3>{editingQuestionId ? "Edit Question" : "Add Question"}</h3>
                    <p>Questions are added to the current section.</p>
                  </div>
                </div>

                <div className="badge badge-muted" style={{ marginBottom: "0.75rem" }}>
                  Current section: {sections.find((s) => s.id === activeSectionId)?.name || "Select from list below"}
                </div>

                <label className="form-label">Question</label>
                <textarea
                  className="form-input"
                  name="question_text"
                  rows="3"
                  value={questionForm.question_text}
                  onChange={handleQuestionChange}
                  placeholder="Enter the question text"
                  required
                  disabled={!activeSectionId}
                />

                {!editingQuestionId && (
                  <div className="portable-hint">
                    <label className="portable-hint-label">
                      <input
                        type="checkbox"
                        checked={questionForm.multi_part}
                        onChange={(e) =>
                          setQuestionForm((prev) => ({
                            ...prev,
                            multi_part: e.target.checked,
                            options: e.target.checked ? [] : prev.options.length ? prev.options : emptyOptions,
                          }))
                        }
                      />
                      Portable Skills (a/b/c model)
                    </label>
                    <p style={{ margin: 0, color: "#5a6b85", fontSize: "0.9rem" }}>
                      Creates 3 questions: (a) Feel Confident 1-5, (b) Feel Joyful Yes/No, (c) Need to Develop Yes/No.
                    </p>
                  </div>
                )}

                {(!questionForm.multi_part || editingQuestionId) && (
                  <div className="option-grid">
                    {questionForm.options.map((opt, index) => (
                      <div key={index} className="option-row">
                        <div className="option-index">{index + 1}</div>
                        <input
                          className="form-input"
                          placeholder={`Option ${index + 1}`}
                          value={opt.text}
                          onChange={(e) => handleOptionChange(index, "text", e.target.value)}
                          required
                          disabled={!activeSectionId}
                        />
                        <input
                          className="form-input"
                          type="number"
                          placeholder="Weight"
                          value={opt.weight}
                          onChange={(e) => handleOptionChange(index, "weight", e.target.value)}
                          required
                          disabled={!activeSectionId}
                        />
                        <div className="option-actions">
                          <button
                            type="button"
                            className="option-action-btn remove"
                            onClick={() => removeOption(index)}
                            disabled={!activeSectionId || questionForm.options.length === 1}
                          >
                            <MinusCircle size={16} />
                          </button>
                          {index === questionForm.options.length - 1 && (
                            <button
                              type="button"
                              className="option-action-btn add"
                              onClick={addOption}
                              disabled={!activeSectionId}
                            >
                              <PlusCircle size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button className="btn-primary" type="submit" disabled={!activeSectionId || questionSaving}>
                  <Save size={16} />
                  {editingQuestionId ? (questionSaving ? "Updating..." : "Update Question") : (questionSaving ? "Saving..." : "Add Question")}
                </button>
              </form>
            </div>
          )}

          {viewMode === "section" && viewSectionId && (
            <div className="panel-card" style={{ marginTop: "1.5rem" }}>
              <div className="panel-card-header">
                <div>
                  <h3>Questions for {sections.find((s) => s.id === viewSectionId)?.name || "Section"}</h3>
                  <p>Add or edit questions. Datatable supports pagination.</p>
                </div>
                <button
                  className="btn-secondary"
                  onClick={startCreateNew}
                  style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                >
                  New Section
                </button>
              </div>

              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Question</th>
                      <th>Options</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const raw = sectionQuestions[viewSectionId] || [];
                      const isLoadingQuestions = loadingSectionQuestions[viewSectionId] === true;
                      if (isLoadingQuestions) {
                        return (
                          <tr>
                            <td colSpan="4" style={{ textAlign: "center" }}>Loading questions...</td>
                          </tr>
                        );
                      }
                      if (raw.length === 0) {
                        return (
                          <tr>
                            <td colSpan="4" style={{ textAlign: "center" }}>No questions yet for this section.</td>
                          </tr>
                        );
                      }
                      const portableGroups = buildPortableGroups(raw);
                      const items = [];
                      portableGroups.forEach((group) => {
                        items.push({ type: "portable", group, index: group.firstIndex ?? 0 });
                      });
                      raw.forEach((q, idx) => {
                        const isPortable = String(q.trait || "") === "portable_group";
                        if (isPortable) return;
                        items.push({ type: "normal", question: q, index: idx });
                      });
                      const ordered = items.sort((a, b) => a.index - b.index);
                      const paged = ordered.slice(
                        ((questionTablePage[viewSectionId] || 1) - 1) * pageSize,
                        (questionTablePage[viewSectionId] || 1) * pageSize
                      );
                      return paged.map((item, idx) => {
                        if (item.type === "portable") {
                          const group = item.group;
                          return (
                            <tr key={`portable-${group.baseText}`}>
                              <td>{((questionTablePage[viewSectionId] || 1) - 1) * pageSize + idx + 1}</td>
                              <td style={{ maxWidth: "340px" }}>{group.baseText}</td>
                              <td>
                                <div className="question-options-mini">
                                  <div className="option-mini">
                                    <span>A) Feel Confident</span>
                                    <span className="option-weight-mini">1-5</span>
                                  </div>
                                  <div className="option-mini">
                                    <span>B) Feel Joyful</span>
                                    <span className="option-weight-mini">Yes/No</span>
                                  </div>
                                  <div className="option-mini">
                                    <span>C) Need to Develop</span>
                                    <span className="option-weight-mini">Yes/No</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <button className="btn-secondary" onClick={() => startEditPortableGroup(viewSectionId, group)}>
                                  <Pencil size={14} />
                                  Edit
                                </button>
                                <button
                                  className="btn-secondary"
                                  onClick={() => deleteQuestion(viewSectionId, group.primary?.id)}
                                  style={{ marginLeft: "0.5rem" }}
                                >
                                  <Trash2 size={14} />
                                  Delete
                                </button>
                              </td>
                            </tr>
                          );
                        }
                        const q = item.question;
                        return (
                          <tr key={q.id}>
                            <td>{((questionTablePage[viewSectionId] || 1) - 1) * pageSize + idx + 1}</td>
                            <td style={{ maxWidth: "340px" }}>{q.question_text}</td>
                            <td>
                              <div className="question-options-mini">
                                {q.options.map((opt, optIdx) => (
                                  <div key={optIdx} className="option-mini">
                                    <span>{opt.text || opt}</span>
                                    <span className="option-weight-mini">{opt.weight ?? 0}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td>
                              <button className="btn-secondary" onClick={() => startEditQuestion(viewSectionId, q)}>
                                <Pencil size={14} />
                                Edit
                              </button>
                              <button
                                className="btn-secondary"
                                onClick={() => deleteQuestion(viewSectionId, q.id)}
                                style={{ marginLeft: "0.5rem" }}
                              >
                                <Trash2 size={14} />
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
              {(() => {
                const raw = sectionQuestions[viewSectionId] || [];
                const totalItems = (() => {
                  const portableGroups = buildPortableGroups(raw).length;
                  const normalCount = (raw || []).filter(
                    (q) => String(q.trait || "") !== "portable_group"
                  ).length;
                  return portableGroups + normalCount;
                })();
                const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
                return (
                  <div className="table-pagination">
                    <button
                      className="btn-secondary"
                      onClick={() =>
                        setQuestionTablePage((prev) => ({
                          ...prev,
                          [viewSectionId]: Math.max(1, (prev[viewSectionId] || 1) - 1),
                        }))
                      }
                      disabled={(questionTablePage[viewSectionId] || 1) === 1}
                    >
                      Previous
                    </button>
                    <span>
                      Page {questionTablePage[viewSectionId] || 1} of {totalPages}
                    </span>
                    <button
                      className="btn-secondary"
                      onClick={() =>
                        setQuestionTablePage((prev) => ({
                          ...prev,
                          [viewSectionId]: Math.min(
                            totalPages,
                            (prev[viewSectionId] || 1) + 1
                          ),
                        }))
                      }
                      disabled={(questionTablePage[viewSectionId] || 1) >= totalPages}
                    >
                      Next
                    </button>
                  </div>
                );
              })()}
            </div>
          )}

          {viewMode === "question" && (
            <div className="panel-card" style={{ marginTop: "1.5rem" }}>
              <div className="panel-card-header">
                <div>
                  <h3>Edit Question</h3>
                  <p>Update the question and option weights.</p>
                </div>
                <button
                  className="btn-secondary"
                  onClick={exitQuestionEdit}
                  style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                >
                  Back to Section
                </button>
              </div>

              <form onSubmit={submitQuestion}>
                <label className="form-label">Question</label>
                <textarea
                  className="form-input"
                  name="question_text"
                  rows="3"
                  value={questionForm.question_text}
                  onChange={handleQuestionChange}
                  placeholder="Enter the question text"
                  required
                />

                {questionForm.multi_part ? (
                  <div className="portable-hint">
                    <p style={{ margin: 0, color: "#2b3b55", fontWeight: 600 }}>
                      Portable Skills (a/b/c model)
                    </p>
                    <div className="portable-hint-list">
                      <div><strong>A)</strong> Feel Confident — 1 (Low) to 5 (High)</div>
                      <div><strong>B)</strong> Feel Joyful — Yes / No</div>
                      <div><strong>C)</strong> Need to Develop — Yes / No</div>
                    </div>
                  </div>
                ) : (
                  <div className="option-grid">
                    {questionForm.options.map((opt, index) => (
                      <div key={index} className="option-row">
                        <div className="option-index">{index + 1}</div>
                        <input
                          className="form-input"
                          placeholder={`Option ${index + 1}`}
                          value={opt.text}
                          onChange={(e) => handleOptionChange(index, "text", e.target.value)}
                          required
                        />
                        <input
                          className="form-input"
                          type="number"
                          placeholder="Weight"
                          value={opt.weight}
                          onChange={(e) => handleOptionChange(index, "weight", e.target.value)}
                          required
                        />
                        <div className="option-actions">
                          <button
                            type="button"
                            className="option-action-btn remove"
                            onClick={() => removeOption(index)}
                            disabled={questionForm.options.length === 1}
                          >
                            <MinusCircle size={16} />
                          </button>
                          {index === questionForm.options.length - 1 && (
                            <button
                              type="button"
                              className="option-action-btn add"
                              onClick={addOption}
                            >
                              <PlusCircle size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button className="btn-primary mt-1.5" type="submit" disabled={questionSaving}>
                  <Save size={16} />
                  {editingQuestionId ? (questionSaving ? "Updating..." : "Update Question") : (questionSaving ? "Saving..." : "Add Question")}
                </button>
              </form>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Admin;
